import { useSearch } from "@helpers/context/SearchContext";
import { plural, t, Trans } from "@helpers/translate";
import React, { useEffect, useMemo, useState } from "react";
import DayPicker from "react-day-picker";
import "react-day-picker/lib/style.css";
import { DateTime } from "luxon";
import { useRouter } from "next/router";
import { searchURL } from "@helpers/searchURL";
import { useLocales } from "@helpers/hooks/useLocales";
import {
  DurationEnum,
  SupplierFiltersResponse,
  TourFiltersResponse,
} from "@graphql/types";
import { Filter } from "./Filter/Filter";
import { InputIncrement } from "@components/ui/Form";

const FilterDate = () => {
  const { filters, setOpenFilter } = useSearch();
  const { locale } = useLocales();
  const router = useRouter();
  const value = filters.date
    ? DateTime.fromISO(filters.date).toJSDate()
    : undefined;

  const initialMonth = value || DateTime.now().toJSDate();

  return (
    <Filter
      id="date"
      title={t({ id: "search.filters.date", message: "Date" })}
      value={
        value
          ? DateTime.fromJSDate(value).toFormat("DD", { locale })
          : undefined
      }
    >
      <DayPicker
        initialMonth={initialMonth}
        firstDayOfWeek={0}
        numberOfMonths={2}
        selectedDays={value ? [value] : []}
        disabledDays={[{ before: new Date() }]}
        fromMonth={new Date()}
        onDayClick={(day) => {
          router.push(
            searchURL({
              ...filters,
              date: DateTime.fromJSDate(day).toFormat("yyyy-MM-dd"),
              page: 1,
            })
          );
          setOpenFilter(undefined);
        }}
        className="w-full"
      />
    </Filter>
  );
};

const FilterPeople = () => {
  const { filters, setOpenFilter } = useSearch();
  const router = useRouter();
  const [people, setPeople] = useState({
    adults: Number(filters.adults || 0),
    children: Number(filters.children || 0),
    infants: Number(filters.infants || 0),
  });

  useEffect(() => {
    setPeople({
      adults: Number(filters.adults || 0),
      children: Number(filters.children || 0),
      infants: Number(filters.infants || 0),
    });
  }, [filters]);

  const peopleTotal = useMemo(() => {
    return people.adults + people.children + people.infants;
  }, [people]);

  const peopleValue = useMemo(() => {
    if (peopleTotal > 0) {
      return t({
        id: "search.filters.people.value",
        message: plural(peopleTotal, {
          one: `${peopleTotal} Person`,
          other: `${peopleTotal} People`,
        }),
      });
    }
    return undefined;
  }, [peopleTotal]);

  return (
    <div>
      <Filter
        id="people"
        title={t({ id: "search.filters.people", message: "People" })}
        value={peopleValue}
        onReset={() => {
          setPeople({
            adults: 0,
            children: 0,
            infants: 0,
          });
          router.push(
            searchURL({
              ...filters,
              adults: 0,
              children: 0,
              infants: 0,
              page: 1,
            })
          );
        }}
        onApply={() => {
          router.push(
            searchURL({
              ...filters,
              ...people,
              ...(people.adults === 0 && { adults: 1 }),
              page: 1,
            })
          );
          setPeople((old) => ({
            ...old,
            ...(old.adults === 0 && { adults: 1 }),
          }));
          setOpenFilter(undefined);
        }}
      >
        <div className="flex justify-between items-center my-4 mb-6 mx-1 md:w-64">
          <div className="mr-4">
            <Trans id="search.filters.adults">Adults</Trans>
          </div>
          <InputIncrement
            min={1}
            value={people.adults}
            onValueChange={(value) =>
              setPeople((old) => ({
                ...old,
                adults: value,
              }))
            }
          />
        </div>
        <div className="flex justify-between items-center my-4 mb-6 mx-1 md:w-64">
          <div className="mr-4">
            <Trans id="search.filters.children">Children</Trans>
          </div>
          <InputIncrement
            value={people.children}
            onValueChange={(value) =>
              setPeople((old) => ({
                ...old,
                children: value,
              }))
            }
          />
        </div>
        <div className="flex justify-between items-center my-4 mb-6 mx-1 md:w-64">
          <div className="mr-4">
            <Trans id="search.filters.infants">Infants</Trans>
          </div>
          <InputIncrement
            value={people.infants}
            onValueChange={(value) =>
              setPeople((old) => ({
                ...old,
                infants: value,
              }))
            }
          />
        </div>
      </Filter>
    </div>
  );
};

const FilterCategories = ({
  categories,
}: {
  categories: { id: number; name: string; slug: string }[];
}) => {
  const { filters } = useSearch();

  const value =
    categories && filters.category
      ? categories.find((category) => category.slug == filters.category)?.name
      : undefined;

  return (
    <Filter
      id="category"
      title={t({ id: "search.filters.category", message: "Category" })}
      value={value}
      lengthyContent={categories.length >= 7}
      disabled={categories.length <= 1 && !value}
    >
      <div className="grid grid-cols-1 lg:grid-cols-2 overflow-y-auto">
        {categories.map((category) => {
          return (
            <Filter.Item
              key={category.id}
              filters={{ ...filters, category: category.slug, page: 1 }} // Reset pagination to page 1 for new category selection
              isActive={category.slug === filters.category}
            >
              {category.name}
            </Filter.Item>
          );
        })}
      </div>
    </Filter>
  );
};

const FilterLanguages = ({
  languages,
}: {
  languages: { id: number; name: string; code: string }[];
}) => {
  const { filters } = useSearch();

  const value =
    languages && filters.language
      ? languages.find((language) => language.code == filters.language)?.name
      : undefined;

  return (
    <Filter
      id="language"
      title={t({ id: "search.filters.language", message: "Language" })}
      value={value}
      lengthyContent={languages.length >= 7}
      disabled={languages.length <= 1 && !value}
    >
      <div className="grid grid-cols-1 lg:grid-cols-2 overflow-y-auto">
        {languages.map((language) => {
          return (
            <Filter.Item
              key={language.id}
              filters={{ ...filters, language: language.code, page: 1 }}
              isActive={language.code === filters.language}
            >
              {language.name}
            </Filter.Item>
          );
        })}
      </div>
    </Filter>
  );
};

const FilterDuration = ({ durations }: { durations: DurationEnum[] }) => {
  const { filters } = useSearch();

  const durationsLocale: { [key: string]: string } = {
    ...(!!durations.find((d) => d === DurationEnum.LessThreeHours) && {
      "less-3-hours": t({
        id: "search.filters.duration.less-3-hours",
        message: "Less than 3 hours",
      }),
    }),
    ...(!!durations.find((d) => d === DurationEnum.HalfDay) && {
      "half-day": t({
        id: "search.filters.duration.half-day",
        message: "Half Day (up to 5 hours)",
      }),
    }),
    ...(!!durations.find((d) => d === DurationEnum.FullDay) && {
      "full-day": t({
        id: "search.filters.duration.full-day",
        message: "Full Day (more than 5 hours)",
      }),
    }),
    ...(!!durations.find((d) => d === DurationEnum.MultipleDays) && {
      "multiple-days": t({
        id: "search.filters.duration.multiple-days",
        message: "Multiple Days",
      }),
    }),
  };

  const value = filters.duration
    ? durationsLocale[filters.duration]
    : undefined;

  return (
    <Filter
      id="duration"
      title={t({ id: "search.filters.duration", message: "Duration" })}
      value={value}
      disabled={Object.keys(durationsLocale).length <= 1 && !value}
    >
      <div className="grid grid-cols-1">
        {Object.keys(durationsLocale).map((key) => {
          return (
            <Filter.Item
              key={key}
              filters={{ ...filters, duration: key, page: 1 }}
              isActive={key === filters.duration}
            >
              {durationsLocale[key]}
            </Filter.Item>
          );
        })}
      </div>
    </Filter>
  );
};

type IProps = {
  defaultFiltersAvailable: TourFiltersResponse | SupplierFiltersResponse;
};

export const Filters = ({ defaultFiltersAvailable }: IProps) => {
  const { filters } = useSearch();

  return (
    <div className="pt-5 pb-6 md:py-8 grid grid-cols-3 gap-2 items-center max-sm:grid-cols-2 lg:flex">
      <FilterDate />
      {["tours", "guides", "transportation"].includes(filters.t) && (
        <FilterPeople />
      )}

      {(filters.t === "tours" ||
        filters.t === "transportation" ||
        filters.t === "online-tours") && (
        <FilterCategories categories={defaultFiltersAvailable.categories} />
      )}

      {(filters.t === "tours" ||
        filters.t === "transportation" ||
        filters.t === "online-tours") && (
        <FilterDuration durations={defaultFiltersAvailable.durations} />
      )}

      <FilterLanguages languages={defaultFiltersAvailable.languages} />
    </div>
  );
};
